//MapThemes.tsx ==============================================================
import "../Util/Prototypes";

import * as Default from "../Default";
import * as DrawerMenu from "../Menus/DrawerMenu";
import * as FeatureLayerLabels from "../Util/FeatureLayerLabels";
import * as MainMap from "../MainMap";
import * as Settings from "../Settings";
import * as Util from "../Util/Util";
import * as WidgetHeading from "./WidgetHeading";
import * as Widgets from "./Widgets";

import { Icon, List, ListItemButton, ListItemIcon, ListItemText, Paper } from "@mui/material";

import React from "react";

export let activeMapTheme: string;
export function setActiveMapThemeName(name: string) {
   activeMapTheme = name;
}

const toolName: string = "Map Themes";

interface iProps {
}

//Render function.
export function Render(props: iProps) {
   Util.logMsg("Top of MapThemes.Render");

   //Set refresh method.
   const [ignored, refresh] = React.useReducer((x: number) => x + 1, 0);

   //Component did mount.
   React.useEffect(() => {
   }, []);

   //Close this tool.
   function close() {
      DrawerMenu.closeTool();
   }

   function setTheme(name: string) {
      activeMapTheme = name;
      setMapTheme(activeMapTheme);
      refresh();
   }

   const scrollDivID: string = toolName.replace(" ", "_") + "_Scroll";

   let selected: boolean;
   const jsx = [];

   const styles: Default.iStyles = {
      heading: { backgroundColor: Settings.headingColor },
   };

   for (const mapTheme of Settings.mapThemes) {
      selected = false;
      if (mapTheme.name === activeMapTheme) {
         selected = true;
      }

      jsx.push(
         <ListItemButton key={Util.uid()} disableGutters={true} selected={selected} onClick={() => setTheme(mapTheme.name)}>
            &nbsp;&nbsp;&nbsp;
            <ListItemIcon><Icon>content_copy</Icon></ListItemIcon>
            <ListItemText primary={mapTheme.name} />
         </ListItemButton>,
      );
   }

   return (
      <Paper>
         <WidgetHeading.Render title={toolName} onClose={close} />

         <div id={scrollDivID} style={Default.styles.appToolDiv}>
            <List component="nav" aria-label="Map themes" disablePadding={true}>
               {jsx}
            </List>
         </div>
      </Paper>
   ); //end return.

} //end function.

//Turn on map theme.
export function setMapTheme(name: string) {
   let i: number;
   let opacity: number;
   let mapLayerfound: boolean;
   let layer: __esri.Layer;
   let subLayersOn: Settings.iSublayer[];
   let sameProjection: boolean;

   Util.logMsg("Setting map theme to: " + name);
   const mapTheme = getMapTheme(name);

   //Process all map services in the map theme.
   if (mapTheme) {

      //Set the base map.  Must be set before operational layers.
      for (const basemap of MainMap.basemaps) {
         mapLayerfound = mapServiceExistInMapTheme(mapTheme, basemap.id);

         if (mapLayerfound) {
            if (MainMap.map.basemap.id !== basemap.id) {

               sameProjection = true;
               if (!MainMap.view2D) {
                  sameProjection = false;
                  if ((basemap.spatialReference && basemap.spatialReference.wkid) === MainMap.activeView.spatialReference.wkid) {
                     sameProjection = true;
                  }
               }

               if (sameProjection) {
                  Widgets.setNextBasemap(basemap);
                  Widgets.toggleBasemap(); //Used to force setting the basemap for different map projections.
               }
            }

            opacity = getMapThemeOpacity(mapTheme, basemap.id);
            if (opacity !== -1) {
               const baseLayer = basemap.baseLayers.getItemAt(0);
               baseLayer.opacity = opacity;
            }
            break;
         }
      }

      //Set operational layers.
      for (i = 0; i < MainMap.map.allLayers.length; i++) {
         layer = MainMap.map.allLayers.getItemAt(i);

         if (isOperationalMap(layer.id)) {
            mapLayerfound = mapServiceExistInMapTheme(mapTheme, layer.id);

            if (mapLayerfound) {
               layer.visible = true;

               if (layer.type === "map-image") {
                  const mapImageLayer = layer as __esri.MapImageLayer;
                  subLayersOn = getMapThemeVisibleSublayers(mapTheme, layer.id);
                  setSublayerVisibility(mapImageLayer.sublayers, subLayersOn);
               }

               opacity = getMapThemeOpacity(mapTheme, layer.id);
               if (opacity === -1) {
                  opacity = 1;
               }

               layer.opacity = opacity;

            } else {
               layer.visible = false;
            }
         }
      }
   }
   //Highlight.updateParameters(); //Update hover sublayers.
   FeatureLayerLabels.startProcessLabels(true);
}

//Is operational map layer.
function isOperationalMap(layerName: string): boolean {
   for (const operMap of Settings.operMaps) {
      if (operMap.id === layerName) {
         return true;
      }
   }

   //Label layers are operational layers as well.
   for (const s of Settings.labelLayers) {
      if (s.labelsLayerName === layerName) {
         return true;
      }
   }

   return false;
}

//Is map service in a map theme.
export function mapServiceExistInMapTheme(mapTheme: Settings.iMapTheme, mapServiceId: string): boolean {
   if (mapTheme) {
      for (const rec of mapTheme.details) {
         if (rec.mapServiceID.Equal(mapServiceId)) {
            return true;
         }
      }
   }
   return false;
}

//Get visible map theme sublayers.
export function getMapThemeVisibleSublayers(mapTheme: Settings.iMapTheme, mapServiceId: string): Settings.iSublayer[] {
   if (mapTheme) {
      for (const rec of mapTheme.details) {
         if (rec.mapServiceID.Equal(mapServiceId)) {
            if (rec.sublayers) {
               return rec.sublayers;
            }
         }
      }
   }
   return [];
}

//Get map theme opacity.
export function getMapThemeOpacity(mapTheme: Settings.iMapTheme, mapServiceId: string): number {
   if (mapTheme) {
      for (const rec of mapTheme.details) {
         if (rec.mapServiceID.Equal(mapServiceId)) {
            return rec.opacity;
         }
      }
   }
   return -1;
}

//Get map theme setting by name.
export function getMapTheme(themeName: string) {
   for (const rec of Settings.mapThemes) {
      if (rec.name.Equal(themeName)) {
         return rec;
      }
   }
   return null;
}

//Set sub layer visibility in a map image service.
export function setSublayerVisibility(sublays: __esri.Collection<__esri.Sublayer>, sublayers: Settings.iSublayer[]) {
   let sublayer: Settings.iSublayer;

   for (const sublay of sublays) {
      sublay.visible = false;
      sublay.opacity = 1;
      sublay.listMode = "hide";

      if (!Settings.securedLayers.Contains(sublay.title)) {
         sublay.listMode = "show";

         sublayer = getSublayer(sublay.title, sublayers);
         if (sublayer) {
            sublay.visible = true;
            sublay.opacity = sublayer.opacity;
            setSublayerParentsOn(sublay);
         }

         //Recursively process the child sublayers.
         if (sublay.sublayers && sublay.sublayers.length > 0) {
            setSublayerVisibility(sublay.sublayers, sublayers);
         }
      }
   }
}

//Set sub layer parents visible in a map image service.
function setSublayerParentsOn(sublay: __esri.Sublayer) {
   let i: number;
   let parent: __esri.Sublayer | __esri.MapImageLayer | __esri.TileLayer;
   let sublayer = sublay;

   for (i = 0; i < 9; i++) {
      if (sublayer.parent) {
         parent = sublayer.parent;

         if (typeof parent.visible !== "undefined") {
            parent.visible = true;
         }
         sublayer = parent as __esri.Sublayer;

      } else {
         return;
      }
   }
}

//Get sublayer.
function getSublayer(name: string, sublayers: Settings.iSublayer[]): Settings.iSublayer {
   let i: number;

   if (sublayers) {
      for (i = 0; i < sublayers.length; i++) {
         if (sublayers[i].name.toLowerCase() === name.toLocaleLowerCase()) {
            return sublayers[i];
         }
      }
   }
   return null;
}
